import { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { collection, addDoc, onSnapshot, query, where, orderBy, deleteDoc, doc, serverTimestamp, getDocs, Timestamp } from "firebase/firestore";
import { toLocalDateString } from "./dateUtils.mjs";

const SITES = ["Paddington Square", "Canary Wharf", "Kings Cross", "Liverpool Street", "Waterloo", "Victoria"];
const SHIFT_TYPES = [
  { label: "Day",     hours: "07:00-19:00", color: "#C9A84C", bg: "#FFF8E7" },
  { label: "Night",   hours: "19:00-07:00", color: "#1A1A2E", bg: "#F0F2F8" },
  { label: "AM",      hours: "06:00-14:00", color: "#2E7D52", bg: "#F0FFF4" },
  { label: "PM",      hours: "14:00-22:00", color: "#7C3AED", bg: "#F3F0FF" },
];

const DAY_NAMES = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const today = new Date();
today.setHours(0,0,0,0);

const getWeekDays = (offset = 0) => {
  const days = [];
  const start = new Date(today);
  const dow = today.getDay() === 0 ? 6 : today.getDay() - 1;
  start.setDate(today.getDate() - dow + offset * 7);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
};

const getMonthDays = (year, month) => {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDow = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  while (days.length % 7 !== 0) days.push(null);
  return days;
};

const isToday = (d) => d && d.toDateString() === today.toDateString();
const fmtShort = (d) => d.toLocaleDateString("en-GB", { day:"2-digit", month:"short" });

const S = {
  page: { padding:"0 24px 24px", color:"#1A1A2E", fontFamily:"system-ui,sans-serif" },
  card: { background:"#FFFFFF", border:"1px solid #E2DDD6", borderRadius:10, padding:16, marginBottom:14 },
  inp:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"8px 12px", borderRadius:7, fontSize:13, width:"100%", boxSizing:"border-box" },
  sel:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"8px 12px", borderRadius:7, fontSize:13 },
  btn:  (bg,col,brd) => ({ background:bg||"#1A1A2E", color:col||"#C9A84C", border:`1px solid ${brd||bg||"#1A1A2E"}`, padding:"7px 16px", borderRadius:7, cursor:"pointer", fontSize:12, fontWeight:700 }),
  label:{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, display:"block", marginBottom:5, textTransform:"uppercase", fontWeight:700 },
};

export default function ShiftScheduler() {
  const [weekOffset, setWeekOffset]   = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [shifts, setShifts]           = useState([]);
  const [officers, setOfficers]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showAdd, setShowAdd]         = useState(false);
  const [view, setView]               = useState("week");
  const [selectedDay, setSelectedDay] = useState(null);
  const [newShift, setNewShift]       = useState({ uid:"", date:"", type:"Day", site:SITES[0], start_time:"07:00", end_time:"19:00", notes:"" });

  const tenantId = auth.currentUser?.tenantId || "osecurity-ltd";

  // Load officers from Firestore
  useEffect(() => {
    const q = query(collection(db, "users"), where("tenant_id","==", tenantId));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ uid:d.id, ...d.data() }));
      setOfficers(list.length ? list : [
        { uid:"T26TNedR4IXsJsYw4sQDxWBA5ez1", name:"Bright Lorence",  role:"Supervisor", email:"brightlorence@gmail.com" },
        { uid:"46xuyKgMwzQ4vlOEHpApeN6fwIl2", name:"Loren Onyinah",   role:"Supervisor", email:"lorenonyinah@outlook.com" },
      ]);
    });
  }, [tenantId]);

  // Load shifts
  useEffect(() => {
    const q = query(collection(db, "shifts"), where("tenant_id","==", tenantId), orderBy("date","asc"));
    return onSnapshot(q, snap => {
      setShifts(snap.docs.map(d => ({ id:d.id, ...d.data(), date: d.data().date?.toDate?.() || new Date(d.data().date) })));
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
  }, [tenantId]);

  const days = getWeekDays(weekOffset);
  const monthDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthDays = getMonthDays(monthDate.getFullYear(), monthDate.getMonth());

  const shiftsFor = (uid, date) => shifts.filter(s => s.uid === uid && s.date.toDateString() === date.toDateString());
  const shiftsOnDay = (date) => shifts.filter(s => s.date.toDateString() === date.toDateString());
  const thisWeekShifts = shifts.filter(s => days.some(d => d.toDateString() === s.date.toDateString()));

  // Coverage: how many officers per day this week
  const coverage = days.map(d => shiftsOnDay(d).length);
  const maxCoverage = Math.max(...coverage, 1);

  // Hours stats
  const totalHoursThisWeek = thisWeekShifts.reduce((sum, s) => {
    const st = SHIFT_TYPES.find(t => t.label === s.type);
    if (!st) return sum;
    const [sh, sm] = (s.start_time || st.hours.split("-")[0]).split(":").map(Number);
    const [eh, em] = (s.end_time   || st.hours.split("-")[1]).split(":").map(Number);
    let h = (eh * 60 + em - sh * 60 - sm) / 60;
    if (h < 0) h += 24;
    return sum + h;
  }, 0);

  const addShift = async () => {
    if (!newShift.date) { alert("Please select a date"); return; }
    if (!newShift.uid && officers.length) newShift.uid = officers[0].uid;
    const officer = officers.find(o => o.uid === newShift.uid) || officers[0];
    if (!officer) { alert("No officers available"); return; }
    try {
      // CONVERGED FIELD NAMES — do not reintroduce officer_uid or shift_type.
      //
      // This document used to carry uid AND officer_uid with identical values,
      // and type AND shift_type likewise. Two conventions, one record. The cost
      // was not storage: the composite index that was actually needed
      // (tenant_id + uid + date) went undeployed while the one nothing needed
      // (tenant_id + officer_uid + date) was live, so every check-in silently
      // recorded unrostered for weeks. Divergent field names produce divergent
      // indexes, and the index is where the divergence bites.
      //
      // uid won because functions/index.js onShiftCreated and
      // notifFunctions.dailyShiftReminder both key off it, and one legacy
      // document carries uid with no officer_uid. type won because every read
      // in this file uses it.
      //
      // Officer-app readers were moved to uid/type in the same change. If you
      // add a reader, use uid and type.
      await addDoc(collection(db, "shifts"), {
        uid:          officer.uid,
        officer_name: officer.name || officer.displayName || officer.email,
        officer_role: officer.role || "Officer",
        // Explicit rather than corrective: the SDK already coerces a JS Date to
        // a Timestamp, so this writes the identical value it did before. It is
        // here so the field's stored type is legible at the call site — reads
        // depend on it (:85 .toDate(), officer app `as Timestamp?`), and a
        // future date-based rule would compare against it.
        date:         Timestamp.fromDate(new Date(newShift.date)),
        type:         newShift.type,
        site:         newShift.site,
        start_time:   newShift.start_time,
        end_time:     newShift.end_time,
        notes:        newShift.notes,
        tenant_id:    tenantId,
        created_by:   auth.currentUser?.email || "admin",
        created_at:   serverTimestamp(),
        notified:     false,
      });
      setShowAdd(false);
      setSelectedDay(null);
      setNewShift({ uid: officers[0]?.uid||"", date:"", type:"Day", site:SITES[0], start_time:"07:00", end_time:"19:00", notes:"" });
    } catch(err) {
      alert("Failed to save shift: " + err.message);
    }
  };

  const removeShift = async (id) => {
    if (!window.confirm("Remove this shift?")) return;
    try {
      await deleteDoc(doc(db,"shifts",id));
    } catch(err) {
      // Deletion is limited to canDeleteRecords() — admin, general_manager,
      // head_of_security — because the roster is a working-time record under
      // WTR 1998 reg. 9 (two-year retention) and the invoice basis. The raw
      // "Missing or insufficient permissions" explains none of that, and the
      // natural next step for whoever sees it is to ask for the rule to be
      // loosened. Non-permission failures keep the underlying message.
      alert(err.code === "permission-denied"
        ? "Shift records are retained as working-time records and can only be removed by an administrator, general manager or head of security."
        : "Failed to remove shift: " + err.message);
    }
  };

  const openAdd = (uid, date) => {
    setNewShift(n => ({
      ...n,
      uid: uid || (officers[0]?.uid||""),
      // Local components, NOT toISOString() — under BST that shifts the
      // seeded date a day early. See src/dateUtils.mjs.
      date: date ? toLocalDateString(date) : n.date,
    }));
    setShowAdd(true);
  };

  const officerInitials = (o) => {
    const n = o.name || o.displayName || o.email || "?";
    return n.split(" ").map(p=>p[0]).join("").toUpperCase().slice(0,2);
  };

  const officerColors = ["#C9A84C","#1A1A2E","#2E7D52","#7C3AED","#C0392B","#185FA5"];

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0 16px" }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:"#1A1A2E" }}>Shift Scheduler</div>
          <div style={{ fontSize:11, color:"#9B8F7A", marginTop:2, letterSpacing:1 }}>
            Rota management · {loading ? "Loading..." : `${shifts.length} shifts total`}
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {["week","month","list"].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              background: view===v ? "#1A1A2E" : "#FFFFFF",
              color:       view===v ? "#C9A84C"  : "#6B6055",
              border:"1px solid #E2DDD6", padding:"6px 14px", borderRadius:7,
              cursor:"pointer", fontSize:12, fontWeight:700, textTransform:"capitalize"
            }}>{v}</button>
          ))}
          <button style={S.btn("#C9A84C","#1A1A2E","#C9A84C")} onClick={() => openAdd("","")}>+ Add Shift</button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:18 }}>
        {[
          ["This Week",    thisWeekShifts.length, "#C9A84C"],
          ["Total Shifts", shifts.length,          "#2E7D52"],
          ["Officers",     officers.length,         "#1A1A2E"],
          ["Hours Sched.", Math.round(totalHoursThisWeek)+"h", "#7C3AED"],
        ].map(([l,v,c]) => (
          <div key={l} style={{ ...S.card, marginBottom:0, borderLeft:`3px solid ${c}`, padding:"14px 16px" }}>
            <div style={{ fontSize:28, fontWeight:800, color:c, lineHeight:1 }}>{v}</div>
            <div style={{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase", marginTop:4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Coverage bar */}
      {view === "week" && (
        <div style={{ ...S.card, marginBottom:14 }}>
          <div style={{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase", fontWeight:700, marginBottom:10 }}>Weekly Coverage</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:6 }}>
            {days.map((d,i) => (
              <div key={i} style={{ textAlign:"center" }}>
                <div style={{ fontSize:10, color: isToday(d) ? "#C9A84C" : "#9B8F7A", fontWeight:700, marginBottom:4 }}>
                  {DAY_NAMES[i]}
                </div>
                <div style={{ height:40, background:"#F4F2EE", borderRadius:5, display:"flex", flexDirection:"column", justifyContent:"flex-end", overflow:"hidden" }}>
                  <div style={{
                    height: `${Math.max(coverage[i]/maxCoverage*100,4)}%`,
                    background: coverage[i]===0 ? "#EF444422" : isToday(d) ? "#C9A84C" : "#1A1A2E",
                    borderRadius:5, transition:"height 0.3s"
                  }}/>
                </div>
                <div style={{ fontSize:11, fontWeight:700, color: coverage[i]===0 ? "#EF4444" : "#1A1A2E", marginTop:3 }}>
                  {coverage[i]}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Week navigation */}
      {(view==="week"||view==="month") && (
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
          <button style={S.btn("#FFFFFF","#6B6055","#E2DDD6")} onClick={() => view==="week" ? setWeekOffset(w=>w-1) : setMonthOffset(m=>m-1)}>
            &larr; {view==="week"?"Prev":""}
          </button>
          <div style={{ flex:1, textAlign:"center", fontSize:14, fontWeight:700, color:"#1A1A2E" }}>
            {view==="week"
              ? `${fmtShort(days[0])} – ${fmtShort(days[6])}${weekOffset===0?" · This Week":""}`
              : `${MONTH_NAMES[monthDate.getMonth()]} ${monthDate.getFullYear()}`
            }
          </div>
          <button style={S.btn("#FFFFFF","#6B6055","#E2DDD6")} onClick={() => view==="week" ? setWeekOffset(w=>w+1) : setMonthOffset(m=>m+1)}>
            {view==="week"?"":""} &rarr;
          </button>
          {(weekOffset!==0||monthOffset!==0) && (
            <button style={S.btn("#F4F2EE","#6B6055","#E2DDD6")} onClick={() => { setWeekOffset(0); setMonthOffset(0); }}>Today</button>
          )}
        </div>
      )}

      {/* WEEK VIEW */}
      {view==="week" && (
        <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
          {/* Header row */}
          <div style={{ display:"grid", gridTemplateColumns:"140px repeat(7,1fr)", background:"#F8F7F4", borderBottom:"1px solid #E2DDD6" }}>
            <div style={{ padding:"10px 14px", fontSize:10, color:"#9B8F7A", letterSpacing:1.5, fontWeight:700 }}>OFFICER</div>
            {days.map(d => (
              <div key={d.toISOString()} style={{
                padding:"10px 8px", textAlign:"center",
                background: isToday(d) ? "#FFF8E7" : "transparent",
                borderLeft:"1px solid #E2DDD6"
              }}>
                <div style={{ fontSize:10, color: isToday(d) ? "#C9A84C" : "#9B8F7A", letterSpacing:1, textTransform:"uppercase", fontWeight:700 }}>
                  {d.toLocaleDateString("en-GB",{weekday:"short"})}
                </div>
                <div style={{ fontSize:16, fontWeight:800, color: isToday(d) ? "#C9A84C" : "#1A1A2E" }}>{d.getDate()}</div>
                <div style={{ fontSize:10, color:"#9B8F7A" }}>{d.toLocaleDateString("en-GB",{month:"short"})}</div>
                {shiftsOnDay(d).length>0 && (
                  <div style={{ fontSize:9, color:"#6B6055", marginTop:2 }}>{shiftsOnDay(d).length} shift{shiftsOnDay(d).length>1?"s":""}</div>
                )}
              </div>
            ))}
          </div>
          {/* Officer rows */}
          {officers.map((officer,oi) => (
            <div key={officer.uid} style={{ display:"grid", gridTemplateColumns:"140px repeat(7,1fr)", borderTop:"1px solid #F0EDE6", background: oi%2===0 ? "#FFFFFF" : "#FAFAF8" }}>
              <div style={{ padding:"12px 14px", display:"flex", alignItems:"center", gap:8, borderRight:"1px solid #E2DDD6" }}>
                <div style={{ width:30, height:30, borderRadius:8, background:officerColors[oi%officerColors.length]+"18", border:`1px solid ${officerColors[oi%officerColors.length]}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:officerColors[oi%officerColors.length], flexShrink:0 }}>
                  {officerInitials(officer)}
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:"#1A1A2E" }}>{(officer.name||officer.email||"").split(" ")[0]}</div>
                  <div style={{ fontSize:9, color:"#9B8F7A" }}>{officer.role||"Officer"}</div>
                </div>
              </div>
              {days.map(d => {
                const dayShifts = shiftsFor(officer.uid, d);
                return (
                  <div key={d.toISOString()} style={{ padding:4, borderLeft:"1px solid #F0EDE6", minHeight:60, background: isToday(d) ? "#FFFBF0" : "transparent", cursor:"pointer" }}
                    onClick={() => { if(!dayShifts.length) openAdd(officer.uid, d); }}>
                    {dayShifts.map(s => {
                      const st = SHIFT_TYPES.find(t=>t.label===s.type)||SHIFT_TYPES[0];
                      return (
                        <div key={s.id} title={`${s.site} · ${s.start_time||st.hours.split("-")[0]}–${s.end_time||st.hours.split("-")[1]}`}
                          style={{ background:st.bg, border:`1px solid ${st.color}44`, borderLeft:`3px solid ${st.color}`, borderRadius:5, padding:"4px 7px", marginBottom:3, cursor:"pointer" }}
                          onClick={e => { e.stopPropagation(); removeShift(s.id); }}>
                          <div style={{ fontSize:10, fontWeight:800, color:st.color }}>{s.type}</div>
                          <div style={{ fontSize:9, color:"#6B6055" }}>{(s.site||"").split(" ")[0]}</div>
                          {s.start_time && <div style={{ fontSize:8, color:"#9B8F7A" }}>{s.start_time}–{s.end_time}</div>}
                        </div>
                      );
                    })}
                    {!dayShifts.length && (
                      <div style={{ height:"100%", minHeight:52, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <span style={{ fontSize:18, color:"#E2DDD6", fontWeight:300 }}>+</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {officers.length===0 && (
            <div style={{ padding:32, textAlign:"center", color:"#9B8F7A" }}>No officers found in your organisation.</div>
          )}
        </div>
      )}

      {/* MONTH VIEW */}
      {view==="month" && (
        <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", background:"#F8F7F4", borderBottom:"1px solid #E2DDD6" }}>
            {DAY_NAMES.map(d => (
              <div key={d} style={{ padding:"10px 8px", textAlign:"center", fontSize:10, color:"#9B8F7A", letterSpacing:1.5, fontWeight:700, textTransform:"uppercase" }}>{d}</div>
            ))}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)" }}>
            {monthDays.map((d,i) => {
              const dayShifts = d ? shiftsOnDay(d) : [];
              const isT = d && isToday(d);
              return (
                <div key={i} style={{
                  minHeight:90, padding:6, borderRight:"1px solid #F0EDE6", borderBottom:"1px solid #F0EDE6",
                  background: !d ? "#FAFAF8" : isT ? "#FFFBF0" : "#FFFFFF",
                  cursor: d ? "pointer" : "default"
                }} onClick={() => d && openAdd("", d)}>
                  {d && (
                    <>
                      <div style={{ fontSize:13, fontWeight: isT ? 800 : 600, color: isT ? "#C9A84C" : "#1A1A2E", marginBottom:4 }}>{d.getDate()}</div>
                      {dayShifts.slice(0,3).map(s => {
                        const st = SHIFT_TYPES.find(t=>t.label===s.type)||SHIFT_TYPES[0];
                        return (
                          <div key={s.id} style={{ background:st.bg, borderLeft:`2px solid ${st.color}`, borderRadius:3, padding:"2px 5px", marginBottom:2, fontSize:9, fontWeight:700, color:st.color }}>
                            {s.officer_name?.split(" ")[0]||"Officer"} · {s.type}
                          </div>
                        );
                      })}
                      {dayShifts.length>3 && <div style={{ fontSize:9, color:"#9B8F7A", marginTop:2 }}>+{dayShifts.length-3} more</div>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LIST VIEW */}
      {view==="list" && (
        <div style={S.card}>
          {shifts.length===0 ? (
            <div style={{ textAlign:"center", padding:48, color:"#9B8F7A" }}>
              {loading ? "Loading shifts..." : "No shifts scheduled. Click + Add Shift to get started."}
            </div>
          ) : (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"#F8F7F4" }}>
                  {["Officer","Date","Type","Hours","Site","Notes",""].map(h => (
                    <th key={h} style={{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase", padding:"10px 12px", borderBottom:"1px solid #EAE6DE", textAlign:"left", fontWeight:700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...shifts].sort((a,b) => a.date-b.date).map(s => {
                  const st = SHIFT_TYPES.find(t=>t.label===s.type)||SHIFT_TYPES[0];
                  return (
                    <tr key={s.id} style={{ borderBottom:"1px solid #F4F0E8" }}>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ fontSize:13, fontWeight:600, color:"#1A1A2E" }}>{s.officer_name}</div>
                        <div style={{ fontSize:10, color:"#9B8F7A" }}>{s.officer_role}</div>
                      </td>
                      <td style={{ padding:"10px 12px", fontSize:12, color:"#1A1A2E" }}>
                        {s.date.toLocaleDateString("en-GB",{weekday:"short",day:"2-digit",month:"short",year:"numeric"})}
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        <span style={{ background:st.bg, color:st.color, borderLeft:`3px solid ${st.color}`, padding:"3px 10px", borderRadius:5, fontSize:11, fontWeight:700 }}>{s.type}</span>
                      </td>
                      <td style={{ padding:"10px 12px", fontSize:12, color:"#6B6055" }}>{s.start_time||st.hours.split("-")[0]}–{s.end_time||st.hours.split("-")[1]}</td>
                      <td style={{ padding:"10px 12px", fontSize:12, color:"#1A1A2E" }}>{s.site}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:"#9B8F7A", maxWidth:120 }}>{s.notes||"—"}</td>
                      <td style={{ padding:"10px 12px" }}>
                        <button style={{ background:"#FEF2F2", color:"#C0392B", border:"1px solid #FECACA", padding:"4px 10px", borderRadius:5, cursor:"pointer", fontSize:11, fontWeight:700 }} onClick={() => removeShift(s.id)}>Remove</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Shift type legend */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:8 }}>
        {SHIFT_TYPES.map(st => (
          <div key={st.label} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:10, height:10, borderRadius:2, background:st.color }}/>
            <span style={{ fontSize:11, color:"#6B6055" }}>{st.label} · {st.hours}</span>
          </div>
        ))}
      </div>

      {/* ADD SHIFT MODAL */}
      {showAdd && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(26,26,46,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#FFFFFF", border:"1px solid #E2DDD6", borderRadius:14, padding:28, width:460, boxSizing:"border-box" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
              <div style={{ fontSize:17, fontWeight:800, color:"#1A1A2E" }}>Add Shift</div>
              <button onClick={() => setShowAdd(false)} style={{ background:"none", border:"1px solid #E2DDD6", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:13, color:"#9B8F7A" }}>Close</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div>
                <label style={S.label}>Officer</label>
                <select style={{ ...S.sel, width:"100%" }} value={newShift.uid} onChange={e => setNewShift(n=>({...n,uid:e.target.value}))}>
                  {officers.map(o => <option key={o.uid} value={o.uid}>{o.name||o.displayName||o.email}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Date</label>
                <input type="date" style={S.inp} value={newShift.date} onChange={e => setNewShift(n=>({...n,date:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>Shift Type</label>
                <select style={{ ...S.sel, width:"100%" }} value={newShift.type}
                  onChange={e => {
                    const st = SHIFT_TYPES.find(t=>t.label===e.target.value);
                    setNewShift(n=>({...n, type:e.target.value, start_time:st?.hours.split("-")[0]||n.start_time, end_time:st?.hours.split("-")[1]||n.end_time}));
                  }}>
                  {SHIFT_TYPES.map(t => <option key={t.label} value={t.label}>{t.label} · {t.hours}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Site</label>
                <select style={{ ...S.sel, width:"100%" }} value={newShift.site} onChange={e => setNewShift(n=>({...n,site:e.target.value}))}>
                  {SITES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Start Time</label>
                <input type="time" style={S.inp} value={newShift.start_time} onChange={e => setNewShift(n=>({...n,start_time:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>End Time</label>
                <input type="time" style={S.inp} value={newShift.end_time} onChange={e => setNewShift(n=>({...n,end_time:e.target.value}))}/>
              </div>
            </div>
            <div style={{ marginTop:14 }}>
              <label style={S.label}>Notes (optional)</label>
              <input style={S.inp} placeholder="e.g. Cover for training day" value={newShift.notes} onChange={e => setNewShift(n=>({...n,notes:e.target.value}))}/>
            </div>
            <div style={{ display:"flex", gap:10, marginTop:22 }}>
              <button style={{ ...S.btn("#FFFFFF","#6B6055","#E2DDD6"), flex:1 }} onClick={() => setShowAdd(false)}>Cancel</button>
              <button style={{ ...S.btn("#C9A84C","#1A1A2E","#C9A84C"), flex:2 }} onClick={addShift}>Save Shift</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
